import { useCallback } from 'react'
import { useReactiveVar } from '@apollo/client'
import { calculatorVar } from '../apollo/reactive-vars'
import {
  EnergyBusResultKey,
  environmentalHeatSubCategory,
  greenEnergySubCategory,
  ResultSubCategory,
  wasteEnergyBasedSubCategory,
} from '../provider/result-categories'
import {
  getFilteredResultsFromContainers,
  getFilteredResultsFromEnergyBus,
} from '../calculators/calculator-result-utility'
import {
  BuildingFragment,
  LocalGridFragment,
  ProviderType,
} from '../generated/graphql'
import { SystemContainerCalculator } from '../calculators/SystemContainerCalculator'

type UseIndicatorIcons = {
  getIndicatorIconsForBuilding: (building: BuildingFragment) => IndicatorIcons
  getIndicatorIconsForLocalGrid: (
    localGrid: LocalGridFragment,
  ) => Partial<IndicatorIcons>
  getIndicatorIconsForEnergyBus: () => Partial<IndicatorIcons>
}

type IndicatorIcons = {
  hasGreenEnergy: boolean
  hasEnvironmentalHeat: boolean
  hasWasteEnergy: boolean
  hasLocalWaterGrid: boolean
  hasLocalElectricity: boolean
}

export function useIndicatorIcons(): UseIndicatorIcons {
  const calculator = useReactiveVar(calculatorVar)

  const getIconEnergyResults = useCallback(
    (systemContainerCalculator: SystemContainerCalculator) => {
      const hasEnergyIn = (subCategory: ResultSubCategory) =>
        !getFilteredResultsFromContainers(subCategory, [
          systemContainerCalculator,
        ]).isEmpty

      const hasGreenEnergy = hasEnergyIn(greenEnergySubCategory)
      const hasEnvironmentalHeat = hasEnergyIn(environmentalHeatSubCategory)
      const hasWasteEnergy = hasEnergyIn(wasteEnergyBasedSubCategory)

      return {
        hasGreenEnergy,
        hasEnvironmentalHeat,
        hasWasteEnergy,
      }
    },
    [],
  )

  const getIndicatorIconsForBuilding = useCallback(
    (building: BuildingFragment): IndicatorIcons => {
      const buildingCalculator = calculator?.findBuilding(building.id)

      if (!buildingCalculator) {
        return {
          hasGreenEnergy: false,
          hasEnvironmentalHeat: false,
          hasWasteEnergy: false,
          hasLocalWaterGrid: false,
          hasLocalElectricity: false,
        }
      }

      const providers = buildingCalculator.getProviders()

      const hasLocalWaterGrid = Boolean(
        providers.find(
          (provider) => provider.type === ProviderType.LocalWaterGrid,
        ),
      )
      const hasLocalElectricity = Boolean(
        providers.find(
          (provider) => provider.type === ProviderType.LocalElectricalGrid,
        ),
      )

      const hasIconEnergies = getIconEnergyResults(buildingCalculator)

      return {
        ...hasIconEnergies,
        hasLocalWaterGrid,
        hasLocalElectricity,
      }
    },
    [calculator, getIconEnergyResults],
  )

  const getIndicatorIconsForLocalGrid = useCallback(
    (localGrid: LocalGridFragment): Partial<IndicatorIcons> => {
      const localGridCalculator = calculator?.findLocalGrid(localGrid.id)

      if (!localGridCalculator) {
        return {
          hasGreenEnergy: false,
          hasEnvironmentalHeat: false,
          hasWasteEnergy: false,
        }
      }

      return getIconEnergyResults(localGridCalculator)
    },
    [calculator, getIconEnergyResults],
  )

  const getIndicatorIconsForEnergyBus = useCallback(() => {
    const energyBusCalculator = calculator?.findEnergyBus()
    if (!energyBusCalculator) {
      return {
        hasEnvironmentalHeat: false,
        hasWasteEnergy: false,
      }
    }

    const hasEnergyIn = (key: EnergyBusResultKey) =>
      !getFilteredResultsFromEnergyBus(energyBusCalculator, key).isEmpty

    const hasEnvironmentalHeat = hasEnergyIn(
      EnergyBusResultKey.HeatFromEnvironment,
    )
    const hasWasteEnergy = hasEnergyIn(EnergyBusResultKey.WasteEnergy)

    return {
      hasEnvironmentalHeat,
      hasWasteEnergy,
    }
  }, [calculator])

  return {
    getIndicatorIconsForBuilding,
    getIndicatorIconsForLocalGrid,
    getIndicatorIconsForEnergyBus,
  }
}
